import { groupBy, partition, snakeCase, zip, zipObject } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, FieldErrors, useFieldArray, UseFormReturn } from 'react-hook-form';

import ExtractionMultipleFieldWriteBacksPanel, {
  orderFieldWritebackForms,
} from 'dashboard/components/ExtractionPanel/ExtractionMultipleFieldWriteBacksPanel';
import ExtractionSingleFieldWritebackPanel, {
  singleFieldWriteBackRequestToFieldWriteBackFormState,
  validateSingleFieldWriteBackPanelForm,
} from 'dashboard/components/ExtractionPanel/ExtractionSingleFieldWriteBackPanel';
import {
  ExtractionCategory,
  ExtractionPanelForm,
  FieldWriteBackPanelForm,
} from 'dashboard/components/ExtractionPanel/util';
import {
  ExtractionRequest,
  ExtractorType,
  FieldWriteBackFormatType,
  FieldWriteBackOption,
  FieldWriteBackRequest,
  FieldWriteBackSelectorType,
  FieldWriteBackSourceType,
} from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Checkbox from 'design-system/components/Checkbox';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

// do NOT change order, order is very important in determining the
// order field writebacks are displayed on dashboard
export const ADDRESS_SUBFIELDS_DISPLAY_NAMES = [
  'Address Street 1',
  'Address Street 2',
  'Address City',
  'Address Subdivision',
  'Address Postal Code',
];

// do NOT change order, order is very important in determining the
// order field writebacks are displayed on dashboard
export const NAME_SUBFIELDS_DISPLAY_NAMES = ['Name First', 'Name Last'];

export const fieldWriteBackRequestsToFormState = (
  extractionRequest: ExtractionRequest,
  fieldWriteBacks: FieldWriteBackRequest[],
  fieldSchema: FieldSchema[],
): Partial<ExtractionPanelForm> => {
  const fieldWriteBackForms = fieldWriteBacks
    .filter((writeback) =>
      writeback.options.some(
        (option) =>
          option['source-type'] === FieldWriteBackSourceType.Extraction &&
          option['source-key'] === extractionRequest['field-name'],
      ),
    )
    .flatMap((writeback) =>
      singleFieldWriteBackRequestToFieldWriteBackFormState(
        extractionRequest,
        writeback,
        fieldSchema,
      ),
    )
    .filter((form): form is NonNullable<FieldWriteBackPanelForm> => form != null);

  return {
    saveToDocumentField: fieldWriteBackForms.length > 0,
    fieldWriteBackForms: fieldWriteBackForms,
  };
};

const filterRequestOutOfFieldWriteBacks = (
  fieldWriteBacks: FieldWriteBackRequest[],
  extractionRequest: ExtractionRequest | null,
) => {
  if (extractionRequest == null) {
    return fieldWriteBacks;
  }
  return fieldWriteBacks
    .map((fieldWriteBack) => ({
      ...fieldWriteBack,
      options: fieldWriteBack.options.filter(
        (option) =>
          // Ensure that we retain any other field write back options that are not related to the extraction
          option['source-type'] !== FieldWriteBackSourceType.Extraction ||
          (option['source-type'] === FieldWriteBackSourceType.Extraction &&
            option['source-key'] !== extractionRequest['field-name']),
      ),
    }))
    .filter((fieldWriteBack) => fieldWriteBack.options.length > 0);
};

const renameFieldWriteBacksFieldName = (
  fieldWriteBackToUpdate: FieldWriteBackRequest,
  extractionName: string,
  oldFieldName: string | undefined,
  newFieldName: string,
) => {
  if (oldFieldName == null || fieldWriteBackToUpdate['field-name'] !== oldFieldName) {
    return [fieldWriteBackToUpdate];
  }

  const [extractionOptions, otherOptions] = partition(
    fieldWriteBackToUpdate.options,
    (o) =>
      o['source-type'] === FieldWriteBackSourceType.Extraction &&
      o['source-key'] === snakeCase(extractionName),
  );
  const updatedFieldWriteBacks = [];
  if (extractionOptions.length > 0) {
    updatedFieldWriteBacks.push({
      'field-name': newFieldName,
      options: extractionOptions,
    });
  }
  if (otherOptions.length > 0) {
    updatedFieldWriteBacks.push({
      'field-name': oldFieldName,
      options: otherOptions,
    });
  }

  // regroup them by field name, since we might have created field writebacks with duplicate field name above
  return Object.entries(groupBy(updatedFieldWriteBacks, (fw) => fw['field-name'])).map(
    ([fieldName, groupedFieldWriteBacks]) => ({
      'field-name': fieldName,
      options: groupedFieldWriteBacks.flatMap((fw) => fw.options),
    }),
  );
};

const renameFieldWriteBacksExtractionName = (
  fieldWriteBackToUpdate: FieldWriteBackRequest,
  oldExtractionName: string | undefined,
  newExtractionName: string,
  subfieldName: string | undefined,
) => {
  if (oldExtractionName == null) {
    return fieldWriteBackToUpdate;
  }

  return {
    ...fieldWriteBackToUpdate,
    options: fieldWriteBackToUpdate.options.map((option) =>
      option['source-type'] === FieldWriteBackSourceType.Extraction &&
      option['source-key'] === oldExtractionName
        ? {
            ...option,
            'source-key': snakeCase(newExtractionName),
            // deprecated, but update this for consistency for now.
            'extraction-name': snakeCase(newExtractionName),
            formatter: {
              format: {
                ...option.formatter.format,
                template: `{{${snakeCase(newExtractionName)}${subfieldName ? '.' + snakeCase(subfieldName) : ''}}}`,
              },
              'normalization-methods-mappings': option.formatter[
                'normalization-methods-mappings'
              ].map((nm) => ({
                ...nm,
                reference: `${snakeCase(newExtractionName)}${subfieldName ? '.' + snakeCase(subfieldName) : ''}`,
              })),
            },
          }
        : option,
    ),
  };
};

const renameFieldWriteBacksSubfieldName = (
  fieldWriteBackToUpdate: FieldWriteBackRequest,
  extractionName: string,
  oldSubfieldName?: string,
  newSubfieldName?: string,
) => {
  if (oldSubfieldName == null || newSubfieldName == null) {
    return fieldWriteBackToUpdate;
  }

  return {
    ...fieldWriteBackToUpdate,
    options: fieldWriteBackToUpdate.options.map((option) =>
      option['source-type'] === FieldWriteBackSourceType.Extraction &&
      option['source-key'] === snakeCase(extractionName) &&
      option.selector?.['specified-field-identifiers']?.includes(oldSubfieldName)
        ? {
            ...option,
            formatter: {
              ...option.formatter,
              'normalization-methods-mappings': option.formatter[
                'normalization-methods-mappings'
              ].map((nm) => {
                if (nm.reference.includes(oldSubfieldName)) {
                  return {
                    ...nm,
                    reference: `${extractionName}.${newSubfieldName}`,
                  };
                } else {
                  return nm;
                }
              }),
            },
            selector: {
              ...option.selector,
              'specified-field-identifiers': [
                ...option.selector['specified-field-identifiers'].filter(
                  (f) => f !== oldSubfieldName,
                ),
                newSubfieldName,
              ],
            },
          }
        : option,
    ),
  };
};

const fieldWriteBackFormStateToUpdatedFieldWritebackAndDocumentFieldRequest = (
  extractionFormData: ExtractionPanelForm,
  newFieldWritebackForm: FieldWriteBackPanelForm,
  oldExtractionRequest: ExtractionRequest,
  oldFieldWritebackForm: FieldWriteBackPanelForm,
  oldFieldWritebackRequest: FieldWriteBackRequest,
): {
  fieldWritebackRequests: FieldWriteBackRequest | FieldWriteBackRequest[];
  documentFieldRequest: FieldSchema | null;
} => {
  let documentFieldRequest: FieldSchema | null = null;
  let updatedFieldWriteBackRequest: FieldWriteBackRequest = oldFieldWritebackRequest;
  let finalFieldWritebackRequests: FieldWriteBackRequest[] | FieldWriteBackRequest =
    updatedFieldWriteBackRequest;

  if (newFieldWritebackForm != null) {
    if (oldExtractionRequest?.['field-name'] !== extractionFormData.fieldName) {
      updatedFieldWriteBackRequest = renameFieldWriteBacksExtractionName(
        updatedFieldWriteBackRequest,
        oldExtractionRequest?.['field-name'],
        extractionFormData.fieldName,
        newFieldWritebackForm.subfieldName,
      );
    }

    if (oldFieldWritebackForm?.subfieldName !== newFieldWritebackForm.subfieldName) {
      updatedFieldWriteBackRequest = renameFieldWriteBacksSubfieldName(
        updatedFieldWriteBackRequest,
        extractionFormData.fieldName,
        oldFieldWritebackForm?.subfieldName,
        newFieldWritebackForm.subfieldName,
      );
    }

    if (
      oldFieldWritebackForm?.documentFieldKey != null &&
      oldFieldWritebackForm.documentFieldKey !== newFieldWritebackForm.documentFieldKey
    ) {
      finalFieldWritebackRequests = renameFieldWriteBacksFieldName(
        updatedFieldWriteBackRequest,
        extractionFormData.fieldName,
        oldFieldWritebackForm.documentFieldKey,
        newFieldWritebackForm.documentFieldKey,
      );

      if (newFieldWritebackForm.isNewDocumentField) {
        switch (newFieldWritebackForm.documentFieldType) {
          case FieldSchemaType.String:
            documentFieldRequest = {
              key: newFieldWritebackForm.documentFieldKey,
              label: newFieldWritebackForm.documentFieldName,
              type: FieldSchemaType.String,
              config: {},
            };
            break;
          case FieldSchemaType.Array:
            documentFieldRequest = {
              key: newFieldWritebackForm.documentFieldKey,
              label: newFieldWritebackForm.documentFieldName,
              type: FieldSchemaType.Array,
              config: {
                'item-schema': {
                  // NB: allows back-end to define default
                  key: null as never,
                  label: null as never,
                  type: FieldSchemaType.String,
                  config: {},
                },
              },
            };
            break;
        }
      }
    } else {
      finalFieldWritebackRequests = updatedFieldWriteBackRequest;
    }
  }

  return {
    fieldWritebackRequests: finalFieldWritebackRequests,
    documentFieldRequest: documentFieldRequest,
  };
};

const fieldWriteBackFormStateToNewFieldWritebackAndDocumentFieldRequest = (
  extractionFormData: ExtractionPanelForm,
  newFieldWritebackForm: FieldWriteBackPanelForm,
  documentFields: FieldSchema[],
): {
  fieldWriteBackRequest: FieldWriteBackRequest | null;
  documentFieldRequest: FieldSchema | null;
} => {
  let documentFieldRequest: FieldSchema | null = null;
  let fieldWritebackRequest: FieldWriteBackRequest | null = null;

  if (extractionFormData.saveToDocumentField) {
    const documentField = documentFields.find(
      (documentField) => documentField.key === newFieldWritebackForm.documentFieldKey,
    );

    let option: FieldWriteBackOption | null = null;
    const documentFieldType = newFieldWritebackForm.isNewDocumentField
      ? newFieldWritebackForm.documentFieldType
      : documentField?.type;
    switch (documentFieldType) {
      case FieldSchemaType.String:
        option = {
          'extraction-name': snakeCase(extractionFormData.fieldName),
          'source-key': snakeCase(extractionFormData.fieldName),
          'source-type': FieldWriteBackSourceType.Extraction,
          selector: {
            type: FieldWriteBackSelectorType.FirstOccurrence,
            'specified-field-identifiers': newFieldWritebackForm.subfieldName
              ? [newFieldWritebackForm.subfieldName]
              : [],
          },
          formatter: {
            format: {
              type: FieldWriteBackFormatType.TemplatedString,
              template: `{{${snakeCase(extractionFormData.fieldName)}${newFieldWritebackForm.subfieldName ? '.' + snakeCase(newFieldWritebackForm.subfieldName) : ''}}}`,
            },
            'normalization-methods-mappings': [],
          },
        };
        break;
      case FieldSchemaType.Array:
        option = {
          'extraction-name': snakeCase(extractionFormData.fieldName),
          'source-key': snakeCase(extractionFormData.fieldName),
          'source-type': FieldWriteBackSourceType.Extraction,
          selector: {
            type: FieldWriteBackSelectorType.SelectAll,
            'specified-field-identifiers': [],
          },
          formatter: {
            format: {
              type: FieldWriteBackFormatType.Array,
              reference: snakeCase(extractionFormData.fieldName),
            },
            'normalization-methods-mappings': [],
          },
        };
        break;
    }

    if (option) {
      fieldWritebackRequest = {
        'field-name': newFieldWritebackForm.documentFieldKey,
        options: [option],
      };

      if (newFieldWritebackForm.isNewDocumentField) {
        switch (documentFieldType) {
          case FieldSchemaType.String:
            documentFieldRequest = {
              key: newFieldWritebackForm.documentFieldKey,
              label: newFieldWritebackForm.documentFieldName,
              type: documentFieldType,
              config: {},
            };
            break;
          case FieldSchemaType.Array:
            documentFieldRequest = {
              key: newFieldWritebackForm.documentFieldKey,
              label: newFieldWritebackForm.documentFieldName,
              type: documentFieldType,
              config: {
                'item-schema': {
                  // NB: allows back-end to define default
                  key: null as never,
                  label: null as never,
                  type: FieldSchemaType.String,
                  config: {},
                },
              },
            };
            break;
        }
      }
    }
  }

  return {
    fieldWriteBackRequest: fieldWritebackRequest,
    documentFieldRequest,
  };
};

const fieldWriteBackFormStateToFieldWritebackAndDocumentFieldRequest = (
  data: ExtractionPanelForm,
  documentFields: FieldSchema[],
  oldExtractionRequest: ExtractionRequest | null,
  newForms: FieldWriteBackPanelForm[],
  oldForms: { form: FieldWriteBackPanelForm; request: FieldWriteBackRequest }[],
) => {
  const allFieldWriteBackRequests: FieldWriteBackRequest[] = [];
  const allDocumentFieldRequests: FieldSchema[] = [];
  zip(newForms, oldForms).forEach(([newForm, oldFormAndRequest]) => {
    if (newForm == null) {
      return;
    }

    let updatedFieldWriteBackRequest;
    let documentFieldRequest;
    // if new form has an empty field key, there we should remove the field writeback
    // by setting updatedFieldWriteBackRequest to null
    if (newForm.documentFieldKey === '' || newForm.documentFieldKey === '') {
      updatedFieldWriteBackRequest = null;
    } else if (oldFormAndRequest == null || oldExtractionRequest == null) {
      ({ fieldWriteBackRequest: updatedFieldWriteBackRequest, documentFieldRequest } =
        fieldWriteBackFormStateToNewFieldWritebackAndDocumentFieldRequest(
          data,
          newForm,
          documentFields,
        ));
    } else {
      ({ fieldWritebackRequests: updatedFieldWriteBackRequest, documentFieldRequest } =
        fieldWriteBackFormStateToUpdatedFieldWritebackAndDocumentFieldRequest(
          data,
          newForm,
          oldExtractionRequest,
          oldFormAndRequest.form,
          oldFormAndRequest.request,
        ));
    }
    if (Array.isArray(updatedFieldWriteBackRequest) && updatedFieldWriteBackRequest.length > 0) {
      allFieldWriteBackRequests.push(...updatedFieldWriteBackRequest);
    } else if (!Array.isArray(updatedFieldWriteBackRequest) && updatedFieldWriteBackRequest) {
      allFieldWriteBackRequests.push(updatedFieldWriteBackRequest);
    }
    if (documentFieldRequest) {
      allDocumentFieldRequests.push(documentFieldRequest);
    }
  });
  return {
    fieldWriteBackRequests: allFieldWriteBackRequests,
    documentFieldRequests: allDocumentFieldRequests,
  };
};

export const formStateToFieldWriteBackAndDocumentFieldRequests = (
  data: ExtractionPanelForm,
  oldValues: Partial<ExtractionPanelForm>,
  oldExtractionRequest: ExtractionRequest | null,
  fieldWriteBacks: FieldWriteBackRequest[],
  documentFields: FieldSchema[],
): {
  fieldWriteBackRequests: FieldWriteBackRequest[];
  documentFieldRequests: FieldSchema[] | null;
} => {
  if (!data.saveToDocumentField) {
    return {
      fieldWriteBackRequests: filterRequestOutOfFieldWriteBacks(
        fieldWriteBacks,
        oldExtractionRequest,
      ),
      documentFieldRequests: [],
    };
  }
  // populate subfields when the user submits and we are translating the from state
  // to field writebacks. the user may populate field writebacks before they populate
  // subfields.
  switch (data.extractionCategory) {
    case ExtractionCategory.Address: {
      const displayNameToSubfieldName = zipObject(ADDRESS_SUBFIELDS_DISPLAY_NAMES, [
        data.addressStreet1Field,
        data.addressStreet2Field,
        data.addressCityField,
        data.addressSubdivisionField,
        data.addressPostalCodeField,
      ]);
      data.fieldWriteBackForms.forEach(
        (form) => (form.subfieldName = displayNameToSubfieldName[form.displayName || '']),
      );
      break;
    }
    case ExtractionCategory.Name: {
      const displayNameToSubfieldName = zipObject(NAME_SUBFIELDS_DISPLAY_NAMES, [
        data.nameFirstField,
        data.nameLastField,
      ]);
      data.fieldWriteBackForms.forEach(
        (form) => (form.subfieldName = displayNameToSubfieldName[form.displayName || '']),
      );
      break;
    }
    default:
      data.fieldWriteBackForms.forEach(
        (fw) =>
          // extraction category stores subfield name for generic extractions
          (fw.subfieldName = data.extractionCategory),
      );
  }

  // we flatten field writebacks with multiple options to multiple field writebacks with a single option
  const flattenedFieldWriteBackRequestsToUpdate = fieldWriteBacks.flatMap((fw) =>
    fw.options.map((option) => ({
      ...fw,
      options: [option],
    })),
  );

  const [_fieldWriteBackRequestsToUpdate, fieldWriteBackRequestsNoUpdate] = partition(
    flattenedFieldWriteBackRequestsToUpdate,
    (fw) =>
      fw.options.some(
        (o) =>
          o['source-type'] === FieldWriteBackSourceType.Extraction &&
          o['source-key'] === snakeCase(oldValues.fieldName),
      ),
  );
  let fieldWriteBackRequestsToUpdate = _fieldWriteBackRequestsToUpdate;

  // if extractor type or extraction category changed, that means that
  // the extraction has changed drastically so we wipe any custom field writebacks associated
  // with the previous extraction
  if (
    oldValues.extractionCategory !== data.extractionCategory ||
    oldValues.extractorType !== data.extractorType
  ) {
    fieldWriteBackRequestsToUpdate = [];
  }

  const fieldWriteBackFormsAndRequestsToUpdate = oldExtractionRequest
    ? fieldWriteBackRequestsToUpdate.flatMap((fieldWriteback) => {
        // since we flattened the field writebacks, there should only be one form state created from it
        const form = singleFieldWriteBackRequestToFieldWriteBackFormState(
          oldExtractionRequest,
          fieldWriteback,
          documentFields,
        )[0];
        return {
          form: form,
          request: fieldWriteback,
          displayName: form?.displayName,
        };
      })
    : [];

  const allFieldWriteBackRequests = fieldWriteBackRequestsNoUpdate;
  const allDocumentFieldRequests: FieldSchema[] = [];
  const newFieldWritebackFormGroupdByDisplayName = groupBy(
    data.fieldWriteBackForms,
    (f) => f.displayName,
  );
  const oldFieldWriteBackFormAndRequestGroupedByDisplayName = groupBy(
    fieldWriteBackFormsAndRequestsToUpdate,
    (f) => f.displayName,
  );
  const displayNames =
    data.extractionCategory === ExtractionCategory.Address
      ? ADDRESS_SUBFIELDS_DISPLAY_NAMES
      : data.extractionCategory === ExtractionCategory.Name
        ? NAME_SUBFIELDS_DISPLAY_NAMES
        : [];

  if (displayNames.length > 1) {
    displayNames.forEach((displayName) => {
      const newForms = (newFieldWritebackFormGroupdByDisplayName[displayName] || []).sort(
        (f1, f2) => orderFieldWritebackForms(data.extractionCategory, f1, f2),
      );
      const oldFormsAndRequests =
        oldFieldWriteBackFormAndRequestGroupedByDisplayName[displayName]?.sort(
          ({ form: f1 }, { form: f2 }) => orderFieldWritebackForms(data.extractionCategory, f1, f2),
        ) || [];
      const { fieldWriteBackRequests, documentFieldRequests } =
        fieldWriteBackFormStateToFieldWritebackAndDocumentFieldRequest(
          data,
          documentFields,
          oldExtractionRequest,
          newForms,
          oldFormsAndRequests,
        );
      allFieldWriteBackRequests.push(...fieldWriteBackRequests);
      allDocumentFieldRequests.push(...documentFieldRequests);
    });
  } else {
    const { fieldWriteBackRequests, documentFieldRequests } =
      fieldWriteBackFormStateToFieldWritebackAndDocumentFieldRequest(
        data,
        documentFields,
        oldExtractionRequest,
        data.fieldWriteBackForms,
        fieldWriteBackFormsAndRequestsToUpdate,
      );
    allFieldWriteBackRequests.push(...fieldWriteBackRequests);
    allDocumentFieldRequests.push(...documentFieldRequests);
  }

  const groupedFieldWriteBacks = Object.entries(
    groupBy(allFieldWriteBackRequests, (fw) => fw['field-name']),
  ).map(([fieldName, groupedFieldWriteBacks]) => ({
    'field-name': fieldName,
    options: groupedFieldWriteBacks.flatMap((fw) => fw.options),
  }));

  return {
    fieldWriteBackRequests: groupedFieldWriteBacks,
    documentFieldRequests: allDocumentFieldRequests,
  };
};

export const validateFieldWriteBacksPanelForm = (
  data: ExtractionPanelForm,
  documentFields: FieldSchema[],
) => {
  const errors: FieldErrors<ExtractionPanelForm> = {};
  if (data.saveToDocumentField) {
    const populatedFieldWritebacks = data.fieldWriteBackForms.filter(
      (form) => form.documentFieldKey != null && form.documentFieldKey !== '',
    );
    const fieldWriteBackErrors = populatedFieldWritebacks.map((form) => {
      const formErrors = validateSingleFieldWriteBackPanelForm(form, documentFields);
      if (Object.keys(formErrors).length > 0) {
        return formErrors;
      }
    });
    if (fieldWriteBackErrors.some((error) => error)) {
      errors.fieldWriteBackForms = fieldWriteBackErrors;
    }
  }
  return errors;
};

interface ExtractionPanelAdvancedProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  vtmplVersion: VerificationTemplateVersionDocument;
  disabled?: boolean;
}

const ExtractionFieldWritebacksPanel = ({
  formReturn,
  vtmplVersion,
  disabled = false,
}: ExtractionPanelAdvancedProps) => {
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { control, watch, clearErrors } = formReturn;
  const extractorType = watch('extractorType');
  const fieldWriteBackForms = watch('fieldWriteBackForms');
  const extractionCategory = watch('extractionCategory');
  const { append, remove } = useFieldArray({ control, name: 'fieldWriteBackForms' });
  const saveToDocumentField = watch('saveToDocumentField');
  const populatePlaceHolderFieldWritebacks = () => {
    const formState = formReturn.getValues();
    const fieldWritebackSubfields = formState.fieldWriteBackForms.map((form) => form.subfieldName);
    const formsToAppend: FieldWriteBackPanelForm[] = [];
    if (extractorType === ExtractorType.InquiryFieldComparison) {
      switch (extractionCategory) {
        case ExtractionCategory.Address: {
          const addressStreet1Field = formState.addressStreet1Field;
          const addressStreet2Field = formState.addressStreet2Field;
          const addressCityField = formState.addressCityField;
          const addressSubdivisionField = formState.addressSubdivisionField;
          const addressPostalCodeField = formState.addressPostalCodeField;
          zip(
            [
              'Address Street 1',
              'Address Street 2',
              'Address City',
              'Address Subdivision',
              'Address Postal Code',
            ],
            [
              addressStreet1Field,
              addressStreet2Field,
              addressCityField,
              addressSubdivisionField,
              addressPostalCodeField,
            ],
          ).forEach(([displayName, subfieldName]) => {
            if (fieldWritebackSubfields.includes(subfieldName)) {
              return;
            }
            formsToAppend.push({
              isNewDocumentField: false,
              documentFieldName: '',
              documentFieldKey: '',
              documentFieldType: FieldSchemaType.String,
              displayName: displayName,
            });
          });
          break;
        }
        case ExtractionCategory.Name: {
          const nameFirstField = formState.nameFirstField;
          const nameLastField = formState.nameLastField;
          zip(['Name First', 'Name Last'], [nameFirstField, nameLastField]).forEach(
            ([displayName, subfieldName]) => {
              if (fieldWritebackSubfields.includes(subfieldName)) {
                return;
              }
              formsToAppend.push({
                isNewDocumentField: false,
                documentFieldName: '',
                documentFieldKey: '',
                documentFieldType: FieldSchemaType.String,
                displayName: displayName,
              });
            },
          );
          break;
        }
        default:
          // populate only when field writeback forms is empty
          if (fieldWriteBackForms.length === 0) {
            formsToAppend.push({
              isNewDocumentField: false,
              documentFieldName: '',
              documentFieldKey: '',
              documentFieldType: FieldSchemaType.String,
            });
          }
      }
    } else {
      // populate only when field writeback forms is empty
      if (fieldWriteBackForms.length === 0) {
        formsToAppend.push({
          isNewDocumentField: false,
          documentFieldName: '',
          documentFieldKey: '',
          documentFieldType: FieldSchemaType.String,
        });
      }
    }
    append(formsToAppend);
  };

  useEffect(() => {
    if (isInitialRender) {
      if (saveToDocumentField) {
        populatePlaceHolderFieldWritebacks();
      }
      setIsInitialRender(false);
      return;
    }
    // if it is not the initial render, that means that extraction category, extractor type
    // or saveToDocumentField has changed and we want to clean up the previous field writebacks
    // and populate it with defaults if necessary.
    remove();
    if (!saveToDocumentField) {
      return;
    }
    populatePlaceHolderFieldWritebacks();
  }, [extractionCategory, extractorType, saveToDocumentField]);

  let fieldWriteBackPanel;
  if (fieldWriteBackForms.length > 1) {
    fieldWriteBackPanel = (
      <ExtractionMultipleFieldWriteBacksPanel
        formReturn={formReturn}
        vtmplVersion={vtmplVersion}
        disabled={disabled}
      />
    );
  } else {
    fieldWriteBackPanel = (
      <ExtractionSingleFieldWritebackPanel
        formReturn={formReturn}
        vtmplVersion={vtmplVersion}
        fieldWriteBackFormIndex={0}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      <Controller
        name='saveToDocumentField'
        control={control}
        render={({ field }) => (
          <Checkbox
            {...field}
            label='Save extraction output to a Document Field'
            checked={field.value}
            onChange={(e) => {
              field.onChange(e);
              remove();
              if (!field.value) {
                clearErrors('fieldWriteBackForms');
              }
            }}
            disabled={disabled}
          />
        )}
      />
      {saveToDocumentField && fieldWriteBackPanel}
    </>
  );
};

export default ExtractionFieldWritebacksPanel;
